import React from 'react';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { selectAvailableOrganizations, selectUser } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { OrganizationStatus, OrganizationType } from 'lib/enums';
import { TabHeader } from './TabHeader';
import { DateRangePickerWithModal } from './DateRangePickerWithModal';
import { OrganizationSelector } from './OrganizationSelector';
import { useReportParams } from '../context/ReportParamsContext';
import { useReportsLoading } from '../context/LoadingContext';

type ReportHeaderProps = {
  title: string;
};

export function ReportHeader({ title }: ReportHeaderProps) {
  const { getDateRange, getOrgIds, updateOrgIds } = useReportParams();
  const { withLoading } = useReportsLoading();

  const { dateRange } = getDateRange();

  const user = useAppSelector(selectUser);
  const availableOrganizations =
    useAppSelector(selectAvailableOrganizations) ?? [];

  const { value: fetchedOrgs } = useAsyncEffect({
    fetchData: async () => {
      const result = await withLoading(
        (async () => {
          if (availableOrganizations?.length) {
            const relevantOrgs = availableOrganizations.filter(org => {
              const orgData = org.data();
              if (!orgData) {
                return false;
              }

              return (
                orgData.organizationType === OrganizationType.newspaper.value &&
                [
                  OrganizationStatus.live.value,
                  OrganizationStatus.in_implementation.value
                ].includes(orgData.organizationStatus)
              );
            });
            return relevantOrgs.map(org => ({
              value: org.id,
              label: org.data()?.name ?? ''
            }));
          }
          return [];
        })()
      );
      return result;
    },
    dependencies: [user?.id],
    errorConfig: {
      service: ColumnService.AUTH_AND_USER_MANAGEMENT,
      message: 'Failed to fetch allowed organizations'
    }
  });

  return (
    <TabHeader title={title}>
      <DateRangePickerWithModal value={dateRange} />
      {fetchedOrgs && fetchedOrgs.length > 1 && (
        <OrganizationSelector
          value={getOrgIds() || []}
          options={fetchedOrgs}
          onChange={updateOrgIds}
        />
      )}
    </TabHeader>
  );
}
